/* eslint-disable react/no-array-index-key */
// Index keys are intentional here: rows are positional final-form paths
// (tiers[i].threshold), so the key must match the path index. Rows are only
// appended/removed, never reordered.
import React from "react";
import { Field } from "react-final-form";
import { Dropdown } from "@my-scoot/component-library-legacy";
import { ExlyDropdown, ExlyInput } from "common/form";
import { AddTierButton, TierDeleteButton } from "./OfferTierActions";
import { ArrayLevelError } from "./ArrayLevelError";
import {
  ARRAY_ERROR_KEYS,
  DEFAULT_TIME_THRESHOLD_UNIT,
  DEFAULT_VALUE_UNIT,
  LTO_FORM_KEYS,
  TIME_UNIT,
  VALUE_UNIT,
} from "../constants";
import {
  addRow,
  asArray,
  emptyTimeTier,
  patchRowAt,
  removeRowAt,
} from "../utils/tierOps";
import { OFFER_DIRECTION } from "../utils/unifiedOffer";
import { validateFlatFinalPrice } from "../limitedOfferValidators";

const TIME_UNIT_OPTIONS = [
  { label: "Days", value: TIME_UNIT.DAYS },
  { label: "Hours", value: TIME_UNIT.HOURS },
];

const VALUE_UNIT_OPTIONS = [
  { label: "%", value: VALUE_UNIT.PERCENT },
  { label: "₹ Flat", value: VALUE_UNIT.FLAT },
];

/**
 * Time-proximity ladder (§5.2): N days/hours before start → price change.
 * structuralDisabled locks shape (add/remove) on edit; disabled locks values.
 */
const TimeProximityTiers = ({
  values = {},
  form,
  metricLabel = "TIME BEFORE START",
  showInternational = false,
  disabled = false,
  structuralDisabled = false,
  originalPrice = null,
}) => {
  const tiers = asArray(values.tiers);
  const valueUnit = values.value_unit || DEFAULT_VALUE_UNIT;
  // Same direction-aware header as TierBuilderTable, read from form values.
  const isSurge = values[LTO_FORM_KEYS.DIRECTION] === OFFER_DIRECTION.SURGE;
  const validateValue = (value) =>
    valueUnit === VALUE_UNIT.FLAT
      ? validateFlatFinalPrice(value, originalPrice, { isSurge })
      : value === "" || value === null || value === undefined
      ? "Required"
      : undefined;

  const currentTiers = () => asArray(form?.getState?.().values?.tiers);
  const addTier = () =>
    form.change(LTO_FORM_KEYS.TIERS, addRow(currentTiers(), emptyTimeTier));
  const removeTier = (idx) =>
    form.change(LTO_FORM_KEYS.TIERS, removeRowAt(currentTiers(), idx));
  // Controlled (non-Field) unit dropdown: writes through a full-array change so
  // it never does a nested `change("tiers[i].threshold_unit")`, which throws
  // when final-form has left tiers as a numeric-keyed object.
  const setRowUnit = (idx, unit) =>
    form.change(
      LTO_FORM_KEYS.TIERS,
      patchRowAt(currentTiers(), idx, {
        [LTO_FORM_KEYS.THRESHOLD_UNIT]: unit,
      })
    );

  const gridCols = showInternational
    ? "tw-grid-cols-[1fr_1fr_1fr_32px]"
    : "tw-grid-cols-[1fr_1fr_32px]";

  return (
    <div className="tw-w-full">
      <span className="tw-block tw-text-[13px] tw-font-semibold tw-mb-2">
        Price Tiers *
      </span>
      {/* value_unit is driven by Offer Type (Amount → ₹ Flat, Percentage → %).
          Hidden Field keeps the backend payload identical — no UI to change it. */}
      <div style={{ display: "none" }}>
        <Field
          name={LTO_FORM_KEYS.VALUE_UNIT}
          component={ExlyDropdown}
          options={VALUE_UNIT_OPTIONS}
          disabled={disabled || structuralDisabled}
          size="small"
        />
      </div>
      <div className="tw-rounded-[10px] tw-border tw-border-solid tw-border-[#E2E4EC] tw-overflow-hidden">
        <div
          className={`tw-grid ${gridCols} tw-gap-2.5 tw-border-0 tw-bg-[#F7F7FB] tw-px-3.5 tw-py-2.5 tw-text-[11px] tw-font-bold tw-text-[#6B7280]`}
        >
          <span>{metricLabel}</span>
          <span>
            {isSurge ? "INCREASE" : "DECREASE"} PRICE{" "}
            {valueUnit === VALUE_UNIT.PERCENT ? "BY (%)" : "TO (₹)"}
          </span>
          {showInternational ? <span>INTERNATIONAL ($)</span> : null}
          <span />
        </div>
        {tiers.map((tierItem, idx) => (
          <div
            // Index-only key: folding the live threshold into the key remounts
            // the row on each keystroke and drops input focus.
            key={`time-tier-${idx}`}
            className={`tw-grid ${gridCols} tw-items-center tw-gap-2.5 tw-mx-3.5 tw-border-x-0 tw-border-b-0 tw-border-t tw-border-solid tw-border-[#E2E4EC] tw-py-2.5`}
          >
            <div className="tw-flex tw-gap-2">
              <div className="tw-flex-1 tw-min-w-0">
                <Field
                  name={`tiers[${idx}].threshold`}
                  component={ExlyInput}
                  type="number"
                  min="1"
                  placeholder="e.g. 7"
                  fullWidth
                  disabled={disabled}
                />
              </div>
              <div className="tw-w-[110px]">
                <Dropdown
                  name={`tiers[${idx}].threshold_unit`}
                  fullWidth
                  size="small"
                  options={TIME_UNIT_OPTIONS}
                  disabled={disabled || structuralDisabled}
                  value={
                    tierItem?.threshold_unit || DEFAULT_TIME_THRESHOLD_UNIT
                  }
                  handleChange={(unit) => setRowUnit(idx, unit)}
                />
              </div>
            </div>
            <Field
              name={`tiers[${idx}].value`}
              component={ExlyInput}
              type="number"
              min="0"
              placeholder="e.g. 15"
              fullWidth
              disabled={disabled}
              validate={validateValue}
            />
            {showInternational ? (
              <Field
                name={`tiers[${idx}].value_international`}
                component={ExlyInput}
                type="number"
                min="0"
                placeholder="e.g. 15"
                fullWidth
                disabled={disabled}
              />
            ) : null}
            <TierDeleteButton
              disabled={disabled || structuralDisabled || tiers.length <= 1}
              onClick={() => removeTier(idx)}
              label="Remove tier"
            />
          </div>
        ))}
      </div>
      <AddTierButton
        disabled={disabled || structuralDisabled}
        onClick={addTier}
      >
        + Add tier
      </AddTierButton>
      <ArrayLevelError errorKey={ARRAY_ERROR_KEYS.TIERS} />
    </div>
  );
};

export { TimeProximityTiers };
